import React, { useState } from 'react';
import {
  McButton,
  McDialog,
  McIcon,
  McNotification,
  McLoadingIndicator,
  McTable,
  McSelect,
  McOption,
  McCheckbox,
  McTextarea,
  McBadge,
} from '@maersk-global/mds-react-wrapper';
import { RefRow, parseRowData } from '../../services/metadataService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InlineDataGridProps {
  cols: any[];
  rows: RefRow[];
  canManage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  mayHaveMore: boolean;
  totalCount: number;
  getRefOptions: (col: any) => { value: string; label: string }[];
  resolveRefValue: (col: any, rawValue: string) => string;
  onSaveNew: (data: Record<string, any>) => Promise<void>;
  onSaveEdit: (rowId: string, data: Record<string, any>, sortOrder: number) => Promise<void>;
  onDeleteRequest: (rowId: string) => void;
  onBulkReplace: (json: string) => void;
  isBulkPending: boolean;
  onLoadMore: () => void;
  onViewHistory?: (rowId: string) => void;
}

// ─── Field editor — uses native inputs so React onChange is guaranteed to fire ──
// McInput fires native `input` events that @lit/react may catch before Lit's own
// handleInputChange sets this.value, so e.target.value reads as "". Native inputs
// have no such timing issue. McSelect/McCheckbox fire custom events (optionselected,
// change) via detail which work reliably, so we keep those.

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '0 12px',
  height: '40px',
  border: '1px solid var(--mds-color-neutral-30, #b0bec5)',
  borderRadius: 'var(--mds-border-radius-medium, 4px)',
  fontSize: '14px',
  fontFamily: 'Barlow, var(--mds-font-family, sans-serif)',
  backgroundColor: 'var(--mds-color-neutral-00, #fff)',
  color: 'var(--mds-color-neutral-80, #1a2332)',
  boxSizing: 'border-box' as const,
  outline: 'none',
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--mds-color-neutral-60, #475569)',
  marginBottom: '4px',
  fontFamily: 'Barlow, var(--mds-font-family, sans-serif)',
};

function FieldEditor({
  col,
  value,
  onChange,
  getRefOptions,
}: {
  col: any;
  value: any;
  onChange: (v: any) => void;
  getRefOptions: (col: any) => { value: string; label: string }[];
}) {
  const label = `${col.label || col.key}${col.required ? ' *' : ''}`;

  // Boolean: McCheckbox fires custom `change` event → e.detail.checked (reliable)
  if (col.type === 'boolean') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
        <McCheckbox
          label={label}
          checked={!!value}
          change={(e: any) => onChange(e.detail?.checked ?? e.target?.checked ?? false)}
        />
      </div>
    );
  }

  // ENUM / ref: McSelect fires `optionselected` with e.detail.value (reliable)
  if (col.type === 'ref') {
    const opts = getRefOptions(col);
    const current = String(value ?? '');
    return (
      <div>
        <span style={fieldLabelStyle}>{label}</span>
        <McSelect value={current} optionselected={(e: any) => onChange(e.detail?.value ?? '')}>
          <McOption value="">— select —</McOption>
          {opts.map(o => <McOption key={o.value} value={o.value}>{o.label}</McOption>)}
        </McSelect>
      </div>
    );
  }
  if (Array.isArray(col.options) && col.options.length > 0) {
    return (
      <div>
        <span style={fieldLabelStyle}>{label}</span>
        <McSelect value={String(value ?? '')} optionselected={(e: any) => onChange(e.detail?.value ?? '')}>
          <McOption value="">— select —</McOption>
          {(col.options as string[]).map(o => <McOption key={o} value={o}>{o}</McOption>)}
        </McSelect>
      </div>
    );
  }

  // All text/number/date fields: native input — React onChange is guaranteed reliable
  return (
    <div>
      <label style={fieldLabelStyle}>{label}</label>
      <input
        type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
        value={col.type === 'date'
          ? (typeof value === 'string' ? value.split('T')[0] : '')
          : (value == null ? '' : String(value))}
        onChange={e => {
          const v = e.target.value;
          onChange(col.type === 'number' ? (v === '' ? null : Number(v)) : v);
        }}
        style={fieldStyle}
      />
    </div>
  );
}

// ─── Row form dialog (add / edit) ────────────────────────────────────────────
// Renders inside an McDialog so it is always centred and visible without scrolling.

function RowForm({
  cols,
  initialData,
  heading,
  isSaving,
  getRefOptions,
  onSave,
  onCancel,
}: {
  cols: any[];
  initialData: Record<string, any>;
  heading: string;
  isSaving: boolean;
  getRefOptions: (col: any) => { value: string; label: string }[];
  onSave: (data: Record<string, any>) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<Record<string, any>>(initialData);

  // Audit fields are server-managed and must never appear in the edit dialog.
  const AUDIT_FIELDS = new Set(['created_at', 'updated_at', 'created_by', 'updated_by']);
  const visibleCols = cols.filter(c => !AUDIT_FIELDS.has(c.key));

  // Immutable columns (e.g. DATETIME/DATE fields auto-detected during CSV import) are
  // server-managed and must not be presented as editable fields.
  const editableCols = visibleCols.filter(c => !c.immutable);
  const immutableCols = visibleCols.filter(c => c.immutable);

  return (
    <McDialog
      open
      heading={heading}
      closed={onCancel}
      width="700px"
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.75rem',
          padding: '0.25rem 0 0.5rem',
        }}
      >
        {editableCols.length > 0 ? editableCols.map(col => (
          <FieldEditor
            key={col.key}
            col={col}
            value={form[col.key]}
            onChange={v => setForm(f => ({ ...f, [col.key]: v }))}
            getRefOptions={getRefOptions}
          />
        )) : cols.length === 0 ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={fieldLabelStyle}>Row data (JSON)</label>
            <input
              style={fieldStyle}
              value={typeof form._raw === 'string' ? form._raw : '{}'}
              placeholder='{"key": "value"}'
              onChange={e => setForm({ _raw: e.target.value })}
            />
          </div>
        ) : (
          <div style={{ gridColumn: '1 / -1', fontSize: '0.875rem', color: 'var(--mds-color-neutral-60)' }}>
            All fields are read-only (server-managed).
          </div>
        )}
      </div>

      {immutableCols.length > 0 && (
        <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: 'var(--mds-color-neutral-02, #fafbfc)', borderRadius: '6px', border: '1px solid var(--mds-color-neutral-15, #e8ecef)' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.78rem', fontWeight: 600, color: 'var(--mds-color-neutral-60)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <McIcon icon="lock" style={{ fontSize: '0.85rem' } as any} />
            Read-only — auto-captured
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
            {immutableCols.map(col => {
              const raw = form[col.key];
              // Format datetime values as human-readable locale string
              let display = '';
              if (raw != null && raw !== '') {
                if (col.backendType === 'DATETIME') {
                  try { display = new Date(String(raw)).toLocaleString(); } catch { display = String(raw); }
                } else {
                  display = String(raw);
                }
              }
              return (
                <div key={col.key}>
                  <span style={fieldLabelStyle}>{col.label || col.key}</span>
                  <input
                    readOnly
                    tabIndex={-1}
                    style={{ ...fieldStyle, backgroundColor: 'var(--mds-color-neutral-05, #f0f2f4)', cursor: 'not-allowed', color: 'var(--mds-color-neutral-50)', fontStyle: display ? 'normal' : 'italic' }}
                    value={display || '—'}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Primary action — sits in the MDS dialog footer slot */}
      <McButton
        slot="primaryAction"
        disabled={isSaving}
        onClick={() => onSave(form)}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </McButton>
      <McButton
        slot="secondaryAction"
        variant="outlined"
        disabled={isSaving}
        onClick={onCancel}
      >
        Cancel
      </McButton>
    </McDialog>
  );
}

// ─── Cell display (inside McTable — purely read-only) ─────────────────────────

function CellDisplay({ col, value, resolveRefValue }: {
  col: any;
  value: any;
  resolveRefValue: (col: any, raw: string) => string;
}) {
  if (col.type === 'boolean') {
    return <McIcon icon={value ? 'check' : 'times'} />;
  }
  const raw = value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
  const display = col.type === 'ref' && raw ? resolveRefValue(col, raw) : raw;
  const isOrphaned = col.type === 'ref' && display.endsWith(' ⚠');
  const truncated = display.length > 60 ? display.slice(0, 60) + '…' : display;
  return (
    <span title={raw} style={isOrphaned ? { color: 'var(--mds-color-feedback-error-text, #b00020)' } : undefined}>
      {truncated}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function InlineDataGrid({
  cols, rows, canManage, isLoading, isFetching, mayHaveMore,
  getRefOptions, resolveRefValue,
  onSaveNew, onSaveEdit, onDeleteRequest, onBulkReplace, isBulkPending, onLoadMore, onViewHistory,
}: InlineDataGridProps) {
  const [addingNew, setAddingNew] = useState(false);
  const [editingRow, setEditingRow] = useState<RefRow | null>(null);
  const [savingNew, setSavingNew] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkJson, setBulkJson] = useState('');

  function defaultForm() {
    const d: Record<string, any> = {};
    cols.forEach(c => { d[c.key] = c.type === 'boolean' ? false : c.type === 'number' ? 0 : ''; });
    return d;
  }

  function editFormFor(row: RefRow) {
    const data = parseRowData(row);
    const d: Record<string, any> = {};
    cols.forEach(c => { d[c.key] = data[c.key] ?? (c.type === 'boolean' ? false : c.type === 'number' ? 0 : ''); });
    return d;
  }

  async function handleSaveNew(data: Record<string, any>) {
    setSavingNew(true);
    try { await onSaveNew(data); setAddingNew(false); }
    finally { setSavingNew(false); }
  }

  async function handleSaveEdit(data: Record<string, any>) {
    if (!editingRow) return;
    setSavingEdit(true);
    try { await onSaveEdit(editingRow.id, data, editingRow.sortOrder ?? 0); setEditingRow(null); }
    finally { setSavingEdit(false); }
  }

  // McTable column definitions — prefix data column IDs to avoid clashing with McTable's own "id" concept
  const ROW_ID_COL = '__rowid';
  const CREATED_AT_COL = '__created_at';
  const UPDATED_AT_COL = '__updated_at';
  const CREATED_BY_COL = '__created_by';
  const HISTORY_COL = '__history';
  const ACTIONS_COL = '__actions';
  const tableColumns = [
    { id: ROW_ID_COL, label: 'Row ID' },
    ...(cols.length > 0
      ? cols.map(c => ({ id: `col__${c.key}`, label: `${c.label || c.key}${c.required ? ' *' : ''}` }))
      : [{ id: 'col__raw', label: 'Data' }]),
    { id: CREATED_AT_COL, label: 'Created At' },
    { id: UPDATED_AT_COL, label: 'Updated At' },
    { id: CREATED_BY_COL, label: 'Created By' },
    { id: HISTORY_COL, label: '' },
    ...(canManage ? [{ id: ACTIONS_COL, label: '' }] : []),
  ];
  const tableData = rows.map(r => ({ id: r.id }));

  return (
    <div>
      {canManage && cols.length === 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          marginBottom: '0.75rem',
          fontSize: '0.875rem',
          color: '#92400e',
        }}>
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>⚠</span>
          <span>
            No fields defined for this table. Go to the <strong>Schema</strong> tab to add fields before adding row data.
          </span>
        </div>
      )}
      {canManage && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <McButton fit="small" onClick={() => { setAddingNew(true); setEditingRow(null); }} disabled={addingNew || !!editingRow || cols.length === 0}>
            <McIcon icon="plus" slot="prefix" />
            Add Row
          </McButton>
        </div>
      )}

      {isLoading && <div style={{ padding: '1.5rem 0' }}><McLoadingIndicator fit="small" /></div>}

      {!isLoading && rows.length === 0 && !addingNew && (
        <p style={{ color: 'var(--mds-color-neutral-60)', fontSize: '0.875rem', padding: '0.75rem 0' }}>
          No rows found.{canManage ? ' Click "Add Row" to get started.' : ''}
        </p>
      )}

      {!isLoading && rows.length > 0 && (
        <McTable data={tableData} columns={tableColumns}>
          {rows.map(row => {
            const data = parseRowData(row);
            const isEditing = editingRow?.id === row.id;
            return (
              <React.Fragment key={row.id}>
                <div slot={`${row.id}_${ROW_ID_COL}`}>
                  <span title={String(row.id)} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--mds-color-neutral-50)' }}>
                    {String(row.id).slice(0, 8)}
                  </span>
                  {isEditing && <McBadge label="editing" style={{ marginLeft: '0.35rem' } as any} />}
                </div>

                {cols.length > 0 ? cols.map(c => (
                  <div slot={`${row.id}_col__${c.key}`} key={c.key}>
                    <CellDisplay col={c} value={data[c.key]} resolveRefValue={resolveRefValue} />
                  </div>
                )) : (
                  <div slot={`${row.id}_col__raw`}>
                    {(() => { const d = JSON.stringify(data); return d.length > 80 ? d.slice(0, 80) + '…' : d; })()}
                  </div>
                )}

                <div slot={`${row.id}_${CREATED_AT_COL}`}>
                  <span title={row.createdAt} style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)' }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </span>
                </div>
                <div slot={`${row.id}_${UPDATED_AT_COL}`}>
                  <span title={row.updatedAt} style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)' }}>
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                  </span>
                </div>
                <div slot={`${row.id}_${CREATED_BY_COL}`}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)' }}>
                    {row.createdBy || '—'}
                  </span>
                </div>

                <div slot={`${row.id}_${HISTORY_COL}`}>
                  <McButton fit="small" variant="plain" appearance="neutral" title="View audit history"
                    onClick={() => onViewHistory?.(row.id)}>
                    <McIcon icon="clock" />
                  </McButton>
                </div>

                {canManage && (
                  <div slot={`${row.id}_${ACTIONS_COL}`} style={{ display: 'flex', gap: '0.25rem' }}>
                    <McButton fit="small" variant="plain" appearance="neutral" title="Edit" disabled={isEditing}
                      onClick={() => { setEditingRow(row); setAddingNew(false); }}>
                      <McIcon icon="pencil" />
                    </McButton>
                    <McButton fit="small" variant="plain" appearance="error" title="Delete"
                      onClick={() => onDeleteRequest(row.id)}>
                      <McIcon icon="trash" />
                    </McButton>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </McTable>
      )}

      {/* Add / Edit forms sit BELOW the table — not in slots, so McInput events reach React state */}
      {addingNew && (
        <RowForm
          cols={cols}
          initialData={defaultForm()}
          heading="New Row"
          isSaving={savingNew}
          getRefOptions={getRefOptions}
          onSave={handleSaveNew}
          onCancel={() => setAddingNew(false)}
        />
      )}

      {editingRow && (
        <RowForm
          cols={cols}
          initialData={editFormFor(editingRow)}
          heading={`Edit — ${String(editingRow.id).slice(0, 8)}…`}
          isSaving={savingEdit}
          getRefOptions={getRefOptions}
          onSave={handleSaveEdit}
          onCancel={() => setEditingRow(null)}
        />
      )}

      {mayHaveMore && (
        <div style={{ marginTop: '0.875rem' }}>
          <McButton variant="outlined" fit="small" onClick={onLoadMore} disabled={isFetching}>
            {isFetching ? 'Loading…' : `Load More (showing ${rows.length})`}
          </McButton>
        </div>
      )}
      {!mayHaveMore && rows.length > 0 && (
        <p style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-50)', marginTop: '0.5rem' }}>
          {rows.length} row{rows.length !== 1 ? 's' : ''} total
        </p>
      )}

      {canManage && (
        <div style={{ marginTop: '0.75rem' }}>
          <McButton variant="plain" fit="small" appearance="neutral" onClick={() => setBulkOpen(o => !o)}>
            <McIcon icon={bulkOpen ? 'chevron-up' : 'chevron-down'} slot="prefix" />
            Bulk Import / Replace (JSON)
          </McButton>
          {bulkOpen && (
            <div style={{ padding: '0.5rem 0' }}>
              <McNotification appearance="warning" heading="Destructive operation"
                body="All existing rows will be soft-deleted and replaced." />
              <p style={{ fontSize: '0.78rem', color: 'var(--mds-color-neutral-50)', margin: '0.5rem 0 0.3rem' }}>
                Paste a JSON array of row data objects:
              </p>
              <McTextarea
                hiddenlabel
                label="Bulk JSON"
                value={bulkJson}
                rows={8}
                placeholder={'[\n  {"key1": "value1"},\n  {"key1": "value2"}\n]'}
                input={(e: any) => setBulkJson(e.detail?.value ?? e.target?.value ?? '')}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <McButton fit="small" onClick={() => { onBulkReplace(bulkJson); setBulkJson(''); setBulkOpen(false); }}
                  disabled={isBulkPending || !bulkJson.trim()}>
                  {isBulkPending ? 'Replacing…' : 'Replace All Rows'}
                </McButton>
                <McButton fit="small" variant="outlined" onClick={() => setBulkJson('')}>Clear</McButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
